import { SolutionContent } from '../components/solutions/SolutionPageLayout';

export const SOLUTIONS: Record<'portals' | 'integrations' | 'automation', SolutionContent> = {
  portals: {
    slug: 'business-portals',
    label: 'Business Portals',
    seoTitle: 'Business Portals',
    seoDescription:
      'Custom business portals that centralize orders, customers, reporting, and documents in one secure dashboard. Built in Whitby, Ontario for teams across Durham Region and beyond.',
    seoKeywords:
      'custom business portal, client portal development, operations dashboard, reporting portal, Whitby Ontario software',
    eyebrow: 'Solution',
    headline: 'One place to',
    highlight: 'run the business',
    lede:
      'A business portal replaces the spreadsheet-and-email shuffle with a single, secure home for your operation. Orders, customers, documents, and reporting live in one place your team and your clients actually use.',
    bullets: [
      'Role-based access for staff, clients, and partners',
      'Live dashboards instead of end-of-month spreadsheets',
      'Document and record storage tied to the right account',
      'Built to match your workflow, not the other way around',
    ],
    blocks: [
      {
        title: 'Client & customer portals',
        body: 'Give customers a branded login to place orders, check status, download documents, and see their history. Every request that currently arrives by email or phone becomes something they handle themselves in seconds.',
      },
      {
        title: 'Internal operations dashboards',
        body: 'One screen for the numbers that matter: orders, inventory, job status, and exceptions. Your team stops exporting CSVs and starts working from live data with the same source of truth.',
      },
      {
        title: 'Reporting that answers questions',
        body: 'Reports built around the questions you actually ask, filterable by location, period, and product line. Schedule them to land in an inbox, or drill into them live whenever you need.',
      },
      {
        title: 'Security from day one',
        body: 'Role-based permissions, audit trails, and encrypted connections are designed in from the first commit, not bolted on before launch. You always know who saw what, and when.',
      },
    ],
    steps: [
      {
        title: 'Map the workflow',
        body: 'We shadow how work actually happens today, then design the portal around it. No forcing your team into someone else\'s idea of process.',
      },
      {
        title: 'Prototype the screens',
        body: 'You click through a working prototype before we build. Layout and flow get settled while changes are still cheap.',
      },
      {
        title: 'Build & integrate',
        body: 'We build in short cycles with weekly demos, connecting the portal to your POS, accounting, and CRM so data flows in on its own.',
      },
      {
        title: 'Launch & train',
        body: 'We migrate your data, train the people who will use it daily, and stay close after launch while the new habit sets in.',
      },
    ],
    fit: [
      'Teams still routing orders, approvals, or documents through email threads and spreadsheets',
      'Businesses whose customers keep calling for status updates you could publish online',
      'Operations that need live numbers by location, product line, or period without waiting on month-end',
      'Companies outgrowing off-the-shelf tools that can\'t model how they actually work',
    ],
  },

  integrations: {
    slug: 'system-integrations',
    label: 'System Integrations',
    seoTitle: 'System Integrations',
    seoDescription:
      'Custom system integrations that connect your POS, CRM, accounting, and inventory into one flow of truth. Monitored, retried, and reconciled. Whitby, Ontario.',
    seoKeywords:
      'system integration services, POS CRM integration, API integration development, data synchronization, Whitby Ontario software',
    eyebrow: 'Solution',
    headline: 'Your tools,',
    highlight: 'finally talking',
    lede:
      'Every system you run holds a piece of the truth. Integrations put those pieces together, so an order entered once flows to inventory, accounting, and reporting without anyone retyping it.',
    bullets: [
      'POS, CRM, accounting, inventory, and e-commerce connected',
      'Two-way sync with conflict handling, not one-way dumps',
      'Automatic retries and alerting when a sync fails',
      'Scheduled reconciliation that catches what events miss',
    ],
    blocks: [
      {
        title: 'Point-to-point, done properly',
        body: 'Your POS and your accounting system agree on every order, automatically. We handle field mapping, deduplication, and the edge cases that show up at 2 a.m., not just the happy path.',
      },
      {
        title: 'Middleware when you need it',
        body: 'For three or more systems, we build a small integration layer that transforms data once and routes it everywhere. Rate limits, retries, and auth live in one place you can monitor.',
      },
      {
        title: 'APIs for the future',
        body: 'Clean, documented APIs on top of your existing systems, so the next tool you adopt connects in days instead of months. You stop paying integration tax on every new purchase.',
      },
      {
        title: 'Monitoring built in',
        body: 'Every integration ships with sync status, failure alerts, and a reconciliation job that compares systems on a schedule and flags discrepancies before your customers notice.',
      },
    ],
    steps: [
      {
        title: 'Audit the stack',
        body: 'We inventory every system, what data it owns, and where it currently gets retyped. You get a plain-language map of the mess before we touch anything.',
      },
      {
        title: 'Design the data flow',
        body: 'We decide which system owns each piece of data, the sync direction, and the cadence. You approve the blueprint before any code is written.',
      },
      {
        title: 'Build & test against real data',
        body: 'Integrations run against anonymized production data in staging, including the failure cases, until syncs are boring and predictable.',
      },
      {
        title: 'Cutover & monitor',
        body: 'We switch over with the old process running in parallel, watch the dashboards, and only retire manual steps once the numbers agree.',
      },
    ],
    fit: [
      'Staff retyping the same order, customer, or invoice into two or more systems',
      'Reports that disagree because each system holds a different version of the truth',
      'Teams held together by nightly CSV exports and someone\'s careful copy-paste',
      'New tools that sat half-adopted because connecting them was never finished',
    ],
  },

  automation: {
    slug: 'workflow-automation',
    label: 'Workflow Automation',
    seoTitle: 'Workflow Automation',
    seoDescription:
      'Workflow automation that removes repetitive busywork: order routing, approvals, notifications, and reporting run themselves, with a logged audit trail. Whitby, Ontario.',
    seoKeywords:
      'workflow automation services, business process automation, task automation, approval workflows, Whitby Ontario software',
    eyebrow: 'Solution',
    headline: 'Busywork,',
    highlight: 'automated away',
    lede:
      'The work your team repeats every day, handled by software. Automation takes the copying, chasing, and reminding off their plate so the hours go into work that actually grows the business.',
    bullets: [
      'Order routing, approvals, and escalations on rails',
      'Notifications that reach the right person at the right time',
      'Scheduled reports assembled and delivered themselves',
      'Every automated step logged for a clean audit trail',
    ],
    blocks: [
      {
        title: 'Repetitive task automation',
        body: 'Data entry, file moves, status updates, follow-up emails. Anything rule-based and repeated, software does faster and without the typo. Your team keeps the exceptions and the judgment calls.',
      },
      {
        title: 'Approval & escalation flows',
        body: 'Requests route to the right person automatically, escalate when they sit too long, and leave a complete audit trail. Nobody chases a signature through the office anymore.',
      },
      {
        title: 'Scheduled everything',
        body: 'Reports compiled and delivered on a schedule. Reminders fired before deadlines, not after. Recurring chores stop depending on someone remembering them on a busy Friday.',
      },
      {
        title: 'Honest about what to automate',
        body: 'We automate processes that are stable and rule-based, and we\'ll tell you plainly when a process should be fixed before it\'s automated. Automating a broken process just breaks it faster.',
      },
    ],
    steps: [
      {
        title: 'Find the hours',
        body: 'We list every repetitive task and what it costs per week, then rank them by payback. You pick where to start with the numbers in front of you.',
      },
      {
        title: 'Stabilize the process',
        body: 'If a process changes weekly, we tighten it first. Automation locks a process in, so it has to be worth locking.',
      },
      {
        title: 'Automate & monitor',
        body: 'We build the automation with logging and failure alerts from the start, so a missed run is visible in minutes, not discovered weeks later.',
      },
      {
        title: 'Measure & expand',
        body: 'After a month we compare actual hours saved against the estimate, then move down the ranked list with the wins funding the next round.',
      },
    ],
    fit: [
      'Teams spending hours a week copying data between systems that could sync themselves',
      'Approvals and sign-offs that stall because nobody remembered to chase them',
      'Recurring reports assembled by hand from three different exports',
      'Onboarding checklists and follow-ups that live in one person\'s head',
    ],
  },
};
